import { StackLayout } from '@leagueplatform/genesis-core';
import {
  type MasonryEngineNodeAction,
  type MasonryEngineActionHandler,
  type MasonryEngineNodeRendererProps,
  type MasonryEngineNode,
  useMasonryEngineActionEmitter,
} from '@leagueplatform/masonry-engine';
import { type MasonryDriverAction } from '@leagueplatform/masonry-renderers';
import { Pagination } from '@leagueplatform/genesis-core-alpha';
import { MASONRY_NAMESPACES } from 'common/constants/masonry';
import { replaceSearchParam } from 'common/utils/replace-search-param.util';

type SetPageAction = MasonryEngineNodeAction<
  'setPage',
  {
    nodeId: string;
    page: number;
  }
>;

const getSetPageAction = (nodeId: string, page: number): SetPageAction => ({
  type: 'setPage',
  namespace: MASONRY_NAMESPACES.PRIOR_AUTHORIZATIONS_STATUS,
  payload: {
    nodeId,
    page,
  },
});

export const setPageActionHandler: MasonryEngineActionHandler<
  MasonryEngineNodeAction<'setPage', Record<string, any>>,
  MasonryDriverAction
> = ({ payload }, { messagingController }) => {
  messagingController.sendMessage(payload.nodeId as string, {
    type: 'reload',
    payload: {
      renderIntermediately: 'loader',
      setQueryParams: (current) => ({
        ...current,
        page: String(payload.page),
      }),
    },
  });
};

type PaginationLayoutNodeProperties = {
  items: Array<MasonryEngineNode>;
  currentPage: number;
  totalPages: number;
};

type PaginationLayoutNode = MasonryEngineNode<
  'paginationLayout',
  PaginationLayoutNodeProperties
>;

type PaginationLayoutProps =
  MasonryEngineNodeRendererProps<PaginationLayoutNode>;

export const PaginationLayout = ({
  items,
  nodeId,
  currentPage,
  totalPages,
}: PaginationLayoutProps) => {
  const invokeAction = useMasonryEngineActionEmitter();
  const setSelectedPage = (selectedPage: number) => {
    replaceSearchParam('page', String(selectedPage));
    invokeAction(getSetPageAction(nodeId, selectedPage));
  };

  if (currentPage > totalPages && totalPages !== 0) setSelectedPage(totalPages);

  return (
    <StackLayout horizontalAlignment="stretch">
      {items}
      <Pagination
        defaultPage={1}
        onPageChange={(selectedPage) =>
          setSelectedPage(selectedPage.pageNumber)
        }
        page={currentPage}
        totalPages={totalPages}
        css={{ justifyContent: 'center', marginBlockStart: '$two' }}
      />
    </StackLayout>
  );
};
